import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import { useNavigate } from 'react-router-dom';

const DataScience = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Data Science & Analytics Services - Business Intelligence | OctalChip',
    description: 'Comprehensive data science services including data analysis, statistical modeling, and business intelligence. Turn data into actionable business insights.',
    keywords: 'data science, data analytics, business intelligence, statistical modeling, data visualization, data mining',
    canonical: 'https://www.octalchip.com/services/ai-ml-services/data-science',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Data Science & Analytics Services",
      "description": "Comprehensive data science services for business intelligence",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const features = [
    { title: 'Data Analysis', description: 'Comprehensive analysis of your data to uncover insights and patterns', icon: 'BarChart' },
    { title: 'Statistical Modeling', description: 'Advanced statistical models for prediction and inference', icon: 'TrendingUp' },
    { title: 'Data Visualization', description: 'Interactive dashboards and visualizations for better understanding', icon: 'PieChart' },
    { title: 'Business Intelligence', description: 'Transform data into actionable business intelligence', icon: 'Briefcase' },
    { title: 'Data Mining', description: 'Discover hidden patterns and relationships in large datasets', icon: 'Search' },
    { title: 'Report Generation', description: 'Automated reports and insights for stakeholders', icon: 'FileText' }
  ];

  const technologies = [
    { name: 'Python', category: 'Language', description: 'Primary language for data science' },
    { name: 'R', category: 'Language', description: 'Statistical computing language' },
    { name: 'SQL', category: 'Language', description: 'Database query language' },
    { name: 'Tableau', category: 'Tool', description: 'Data visualization platform' },
    { name: 'Power BI', category: 'Tool', description: 'Business intelligence tool' },
    { name: 'Jupyter', category: 'Platform', description: 'Interactive data science notebooks' }
  ];

  const useCases = [
    { title: 'Business Intelligence', description: 'Transform data into actionable business insights', icon: 'Briefcase' },
    { title: 'Market Research', description: 'Analyze market trends and customer behavior', icon: 'TrendingUp' },
    { title: 'Customer Analytics', description: 'Understand customer preferences and behavior', icon: 'Users' },
    { title: 'Operational Analytics', description: 'Optimize operations based on data insights', icon: 'Settings' },
    { title: 'Financial Analysis', description: 'Financial modeling and risk analysis', icon: 'DollarSign' },
    { title: 'Performance Dashboards', description: 'Real-time dashboards for monitoring KPIs', icon: 'Monitor' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO title={seoData.title} description={seoData.description} keywords={seoData.keywords} canonical={seoData.canonical} structuredData={seoData.structuredData} />
      <Header />
      {/* Enhanced Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00d690 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-2xl mb-8 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <Icon name="BarChart" size={48} className="text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-800 mb-6 leading-tight">Data Science &<span className="block text-[#00d690] mt-2">Analytics</span></h1>
            <div className="w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-8"></div>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">Comprehensive data science services including data analysis, statistical modeling, and business intelligence. Turn data into actionable business insights.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="primary" onClick={() => navigate('/services#share-your-requirements')} className="px-10 py-4 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all">Start Your Project</Button>
              <Button variant="outline" onClick={() => navigate('/ai-ml-services')} className="px-10 py-4 text-lg border-2">View All AI Services</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">22+</div>
              <div className="text-gray-600 font-medium">Data Projects</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">3-8</div>
              <div className="text-gray-600 font-medium">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">100%</div>
              <div className="text-gray-600 font-medium">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Key Features</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive features designed to deliver exceptional data science solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-xl flex items-center justify-center mb-6 group-hover:from-[#00d690] group-hover:to-[#00b876] transition-all duration-300">
                  <Icon name={feature.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Technologies We Master</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We work with the latest and most powerful data science technologies to build comprehensive analytics solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">{tech.name}</span>
                  <span className="text-xs font-medium text-[#00d690] bg-[#00d690]/10 px-2 py-1 rounded">{tech.category}</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gradient-to-br from-[#00d690]/5 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">What We Build</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From business intelligence to performance dashboards, we deliver data science solutions for every business need
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group border-l-4 border-[#00d690]">
                <div className="w-14 h-14 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00d690] transition-all duration-300">
                  <Icon name={useCase.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Development Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures quality, transparency, and timely delivery
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d690] to-[#00b876]"></div>
              
              <div className="space-y-12">
                {[
                  {
                    step: '01',
                    title: 'Requirements Analysis & Data Discovery',
                    description: 'We analyze your business needs, discover available data sources, and identify key questions to answer with data science',
                    icon: 'Search'
                  },
                  {
                    step: '02',
                    title: 'Data Collection & Preparation',
                    description: 'We collect data from various sources, clean and preprocess it, handle missing values, and prepare for analysis',
                    icon: 'Database'
                  },
                  {
                    step: '03',
                    title: 'Analysis & Modeling',
                    description: 'We perform statistical analysis, build predictive models, create visualizations, and extract meaningful insights',
                    icon: 'Code'
                  },
                  {
                    step: '04',
                    title: 'Validation & Testing',
                    description: 'We validate models and insights, test hypotheses, and ensure accuracy and reliability of findings',
                    icon: 'CheckCircle'
                  },
                  {
                    step: '05',
                    title: 'Visualization & Reporting',
                    description: 'We create interactive dashboards, generate reports, and present insights in actionable formats for stakeholders',
                    icon: 'Rocket'
                  },
                  {
                    step: '06',
                    title: 'Implementation & Monitoring',
                    description: 'We implement data-driven solutions, set up monitoring systems, and provide ongoing analytics support',
                    icon: 'BarChart'
                  }
                ].map((step, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                          <Icon name={step.icon} size={24} className="text-[#00d690]" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-lg">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Our Data Science Services?</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Expert data scientists with proven track record in analytics and modeling',
                'Custom solutions tailored to your specific business needs and data',
                'End-to-end development from data collection to actionable insights',
                'Scalable analytics systems that handle large volumes of data',
                'Interactive dashboards and visualizations for better understanding',
                'Integration with existing systems and business intelligence tools',
                'Cost-effective data science solutions with measurable ROI',
                'Ongoing support and continuous analytics improvement'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#00d690] rounded-full flex items-center justify-center">
                    <Icon name="Check" size={20} className="text-white" />
                  </div>
                  <p className="text-lg text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Ready to Build Your Data Science Solution?
            </h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Let's discuss your project requirements and create a solution that drives your business forward. 
              Get a free consultation and quote today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/services#share-your-requirements')}
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project
              </button>
              <button
                onClick={() => navigate('/ai-ml-services')}
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 rounded-lg transition-all duration-300"
              >
                View All AI Services
              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default DataScience;

